import { useMemo } from 'react';
import { getBackendNameArr, getBackendNameList, getDimensionNameList } from '../utils';

/**
 * Hook to determine if the "Add Dimension" button should be disabled.
 * Disables the button if:
 * 1. Not all dimension selectors have values selected
 * 2. The selected combination already exists in dimensionData
 *
 * Handles both single dimensions and dimension combinations by flattening
 * backend names that contain the separator '&&&'.
 */
export default function useCreateDisable(selectValArr, dimensionData) {
  const disableCreateBtn = useMemo(() => {
    // New: Disable if any selector is empty or missing .value
    if (!selectValArr.every((val) => val && val.value)) {
      return true;
    }

    const dimensionsSelect = getBackendNameArr(selectValArr);

    // Flatten combination backend names for proper comparison
    const flattenedBackendNames: string[] = [];
    dimensionsSelect.forEach((backendName) => {
      if (backendName && backendName.includes('&&&')) {
        // This is a combination, split it into individual backend names
        flattenedBackendNames.push(...getBackendNameList(backendName));
      } else if (backendName) {
        // This is a single backend name
        flattenedBackendNames.push(backendName);
      }
    });

    const hasAllSelectVal = flattenedBackendNames.every(Boolean);

    if (!hasAllSelectVal) {
      return true; // Disable if not all values are selected
    }

    // Flatten the selected display names (values). A combination selector's value is a
    // NAME_SEPARATOR-joined string (e.g. "CDN,City"), so split each one into tokens.
    const flattenedDisplayNames: string[] = [];
    selectValArr.forEach((val) => {
      if (val?.value) {
        flattenedDisplayNames.push(...getDimensionNameList(val.value));
      }
    });

    // Order-independent membership check for equal-length token lists.
    const sameTokens = (listA: string[], listB: string[]) =>
      listA.length > 0 &&
      listA.length === listB.length &&
      listA.every((item) => listB.includes(item));

    // Check if this combination already exists. Compare by backendName AND by display name:
    // dropdown selections carry mapping-key backendName forms (e.g. CURRENT_CDN, ISP) while the
    // stored config uses the cardinality/CCS form (e.g. CDN, GEO_ISP), so a backendName-only
    // match misses those duplicates. Both sides resolve to the same pulse5Label display name, so
    // the name comparison reliably catches the remaining duplicates regardless of backendName form.
    const hasExistingCombination = dimensionData.some(({ backendName, name }) => {
      const backendNameList = getBackendNameList(backendName);
      const backendMatch = sameTokens(backendNameList, flattenedBackendNames);

      const nameList = name ? getDimensionNameList(name) : [];
      const nameMatch = sameTokens(nameList, flattenedDisplayNames);

      return backendMatch || nameMatch;
    });

    return hasExistingCombination;
  }, [selectValArr, dimensionData]);

  return disableCreateBtn;
}
